/**
 * Core game type definitions from PRD Section 2.3
 */

// Vector2 for positions and directions
export interface Vector2 {
  x: number;
  y: number;
}

// HSL color representation
export interface HSLColor {
  h: number; // 0-360
  s: number; // 0-100
  l: number; // 0-100
}

// Microscope System Types (PRD 2.3.1)
export type StainType = 'none' | 'gram' | 'methylene-blue' | 'iodine';

export interface MicroscopeState {
  zoomLevel: number;
  position: Vector2;
  focusDepth: number;
  stainType: StainType;
  scannerActive: boolean;
}

export enum ZoomThreshold {
  ORGANISM = 1,
  TISSUE = 10,
  CELL = 100,
  ORGANELLE = 400,
  MOLECULAR = 1000,
}

// Scanner System Types (PRD 2.3.2)
export type CellCount = number | 'none' | 'single' | 'multiple';

// Cell types for scanner classification
export type ScannerCellType = 'prokaryotic' | 'eukaryotic' | 'eukaryotic-plant' | 'none';

// Cell types for procedural generation and visualization
export type CellType = 'animal' | 'plant' | 'bacteria' | 'protist' | 'fungi' | 'archaea';

export interface ScanResult {
  isLiving: boolean;
  cellCount: CellCount;
  cellType: ScannerCellType;
  detectedFunctions: LifeFunction[];
  confidence: number;
}

// Life Functions (PRD 2.3.2)
export enum LifeFunction {
  METABOLISM = 'METABOLISM',
  REPRODUCTION = 'REPRODUCTION',
  RESPONSE_TO_STIMULI = 'RESPONSE_TO_STIMULI',
  HOMEOSTASIS = 'HOMEOSTASIS',
  GROWTH = 'GROWTH',
  WASTE_REMOVAL = 'WASTE_REMOVAL',
  GAS_EXCHANGE = 'GAS_EXCHANGE',
}

// Classification System Types (PRD 2.3.3)
export enum EvidenceType {
  STRUCTURAL = 'STRUCTURAL',
  FUNCTIONAL = 'FUNCTIONAL',
  COMPOSITIONAL = 'COMPOSITIONAL',
  BEHAVIORAL = 'BEHAVIORAL',
  CONTEXTUAL = 'CONTEXTUAL',
}

export enum Classification {
  LIVING = 'LIVING',
  NON_LIVING = 'NON_LIVING',
  UNICELLULAR = 'UNICELLULAR',
  MULTICELLULAR = 'MULTICELLULAR',
}

export interface EvidenceCard {
  id: string;
  type: EvidenceType;
  description: string;
  supportsClassification: Classification[];
  contradictsClassification: Classification[];
}

export interface ClassificationResult {
  correct: boolean;
  feedback: string;
  expectedClassification?: Classification;
}

// Game Progress Types
export interface LevelProgress {
  levelId: string;
  completed: boolean;
  score: number;
  stars: number; // 0-3
  bestTime?: number;
}

export interface ChapterProgress {
  chapterId: string;
  levelsCompleted: number;
  totalLevels: number;
  unlocked: boolean;
  bossDefeated: boolean;
}

// Settings Types
export interface GameSettings {
  audio: AudioSettings;
  accessibility: AccessibilitySettings;
  gameplay: GameplaySettings;
}

export interface AudioSettings {
  masterVolume: number;
  ambientVolume: number;
  effectsVolume: number;
  musicEnabled: boolean;
  sfxEnabled: boolean;
}

export interface AccessibilitySettings {
  colorMode: 'normal' | 'protanopia' | 'deuteranopia' | 'tritanopia' | 'high_contrast';
  fontSize: 'small' | 'medium' | 'large' | 'extra_large';
  reduceMotion: boolean;
  highContrastUI: boolean;
  screenReaderMode: boolean;
  audioDescriptions: boolean;
  keyboardNavigation: boolean;
  dwellClickEnabled: boolean;
  dwellTime: number;
}

export interface GameplaySettings {
  difficulty: 'easy' | 'normal' | 'hard';
  tutorialsEnabled: boolean;
  hintsEnabled: boolean;
}
